import { FC, useState, useCallback } from 'react'
import Map from './Map';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import ToggleMapButton from './ToggleMapButton';
import { DeleteContactSvg, MaximizeSvg, UnbookContactSvg } from '../Svgs';
import { get_contact_pass_details } from '../utils';
import { ContactStation } from '../../../../types';
import { PlusIcon } from '../../../../../CommonComponents/svgs/Svgs';
import ConfirmationDialog from '../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import useContacts from '../../../../hooks/useContacts';
import useUrlParams from '../../../../hooks/useUrlParams';
import EmptyPage from '../../../../custom_components/EmptyPage';
import useScenario from '../../../../hooks/useScenario';
import { isOperateMode, isSummaryMode } from '../../../../hooks/utils';
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../../simulate/constants';

// Define Props interface
interface Props {
    set_show_modal: () => void;
    all_contacts: {
        previous_contacts: null | ContactStation[],
        ongoing_contacts: null | ContactStation[],
        upcoming_contacts: null | ContactStation[],
        current_time: null | number,
    };
    selected_contact: {
        contact_type: string;
        contact_data: ContactStation,
    } | any | null;
    setIsModalOpen: React.Dispatch<React.SetStateAction<'create' | 'book' | null>>;
}

// Contact status badge component
const ContactStatusBadge: FC<{
    type: string;
    contact: ContactStation;
    currentTime: number;
    setIsModalOpen: (value: 'create' | 'book' | null) => void;
    dashboard?: string
}> = ({ type, contact, currentTime, setIsModalOpen, dashboard }) => {
    // Separate states for delete/unbook dialogs
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showUnbookDialog, setShowUnbookDialog] = useState(false);

    const { unbook_contact, unbook_delete_contact } = useContacts();

    const status = contact?.status;
    const isNotBooked = status === 'NotBooked';
    const isBooked = status === 'Booked';
    const isUpcoming = type === 'Upcoming';
    const isCustom = contact?.['IsAntarisGsiteProvider'];

    const { remaining } = get_contact_pass_details(type, contact, currentTime);
    const bandNames = contact?.bandInfo?.bandName;

    const initials = bandNames
        ?.split(',')
        .map(band => band.trim()[0].toUpperCase())
        .join('');

    const bandContainerWidth = initials?.length > 2 ? '2.2rem' : '1.5rem';
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)
    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
    const actionUnbookDeleteMargin = operateMode ? "0 0.5rem" : '0'


    const handleDelete = () => {
        if (contact?.uniqueId) {
            unbook_delete_contact(contact.uniqueId);
        }
        setShowDeleteDialog(false);
    };

    const handleUnbook = () => {
        if (contact?.uniqueId) {
            unbook_contact(contact.uniqueId);
        }
        setShowUnbookDialog(false);
    };
    return (
        <>
            <div className={`contact__status__badge gap-2 ${type}`}>
                <CustomToolTip
                    title={bandNames}
                    placement='bottom'
                >
                    <div
                        className={`csb-badge__avatar ${type}`}
                        style={{ width: bandContainerWidth }}
                    >
                        {initials}
                    </div>
                </CustomToolTip>
                <div className="csb-badge__info">
                    <span className={`csb-badge__title ${type}`}>{type}</span>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex' }}>
                    {remaining && (
                        <span className={`csb-badge__time-pill ${type}`} style={{ margin: actionUnbookDeleteMargin }}>{remaining}</span>
                    )}
                    {deploymentType == SCENARIO_DEPLOYMENT_TYPES.OPERATE &&
                        <>
                            {(isNotBooked && isUpcoming) && (
                                <button
                                    type="button"
                                    title="Book"
                                    className="csb-badge__action"
                                    onClick={() => setIsModalOpen('book')}
                                >
                                    <PlusIcon />
                                </button>
                            )}

                            {isUpcoming && isBooked && (
                                <>
                                    {isCustom && (
                                        <CustomToolTip placement='top' title='Delete'>
                                            <button
                                                type="button"
                                                className="csb-badge__action-delete"
                                                onClick={() => setShowDeleteDialog(true)}
                                            >
                                                <DeleteContactSvg />
                                            </button>
                                        </CustomToolTip>
                                    )}
                                    <CustomToolTip placement='top' title='UnBook'>
                                        <button
                                            type="button"
                                            className="csb-badge__action"
                                            onClick={() => setShowUnbookDialog(true)}
                                        >
                                            <UnbookContactSvg />
                                        </button>
                                    </CustomToolTip>
                                </>
                            )}
                        </>
                    }
                </div>

            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                type="delete"
                open={showDeleteDialog}
                title="Delete Ground Contact?"
                description="This ground contact will be deleted from the list of ground contacts."
                onCancel={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
                confirm_button_text="Confirm"
            />

            {/* Unbook Confirmation Dialog */}
            <ConfirmationDialog
                type="cancel"
                open={showUnbookDialog}
                title="Unbook Ground Contact?"
                description="This ground contact will be unbooked from the list of ground contacts."
                onCancel={() => setShowUnbookDialog(false)}
                onConfirm={handleUnbook}
                confirm_button_text="Confirm"
            />
        </>
    );
};




// Contact details row component
const GroundContactDetail: FC<{
    label: string;
    value: string | React.ReactNode
}> = ({ label, value }) => (
    <div className='ground__contact__details'>
        <span>{label}</span>
        <span>{value}</span>
    </div>
);

// Contact card component
const ContactCard: FC<{
    type: string;
    contact: ContactStation;
    currentTime: number;
}> = ({ type, contact, currentTime }) => {
    return (

        <div className={`contact-card ${type.toLowerCase()}`}>
            <span className="card-left">
                {get_contact_pass_details(type, contact, currentTime)?.time}
            </span>
            <span className="card-right">
                {type}
                <div className="groundstation-name opacity-50">
                    {contact?.groundStationName || '—'}
                </div>
            </span>
        </div>
    );
};


const RightSidePanel: FC<Props> = ({
    set_show_modal,
    all_contacts,
    selected_contact,
    setIsModalOpen
}) => {
    const {
        previous_contacts,
        ongoing_contacts,
        upcoming_contacts,
        current_time
    } = all_contacts;

    const [activeTab, setActiveTab] = useState('contacts');
    const { dashboard } = useUrlParams()
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)
    const summaryMode = isSummaryMode(dashboard, scenario)
    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()

    // Get contact details
    const getContactDetails = useCallback(() => {
        if (!selected_contact || !current_time) return null;
        return get_contact_pass_details(
            selected_contact.contact_type,
            selected_contact.contact_data,
            current_time
        );
    }, [selected_contact, current_time]);

    const contactDetails = getContactDetails();
    const contactData = selected_contact?.contact_data;
    // Render contact cards for each type
    const renderContactCards = () => {
        if (!current_time) return null;

        const contacts = [
            { type: 'Ongoing', data: ongoing_contacts?.[0] },
            { type: 'Previous', data: previous_contacts?.[0] },
            { type: 'Upcoming', data: upcoming_contacts?.[0] },
        ];

        return (
            <div className="contacts-container">
                {contacts.map(({ type, data }) => (
                    data && (
                        <ContactCard
                            key={type}
                            type={type}
                            contact={data}
                            currentTime={current_time}
                        />
                    )
                ))}
            </div>
        );
    };
    if (!previous_contacts && !ongoing_contacts && !upcoming_contacts) {
        return (
            <div className='ground__container__right__container'>
                <EmptyPage content='No Ground Contacts' ></EmptyPage>
            </div>
        )
    }
    return (
        <div className={`ground__container__right__container ${selected_contact?.contact_type !== 'Ongoing' ? 'not-active' : ''}`}>
            {/* Header Section */}
            <div className='d-flex flex-column gap-2'>
                <div className='contact__details__header'>
                    <span className='contact__details__header__title'>
                        <CustomToolTip
                            title={
                                contactData?.antennaId
                                    ? contactData.antennaId
                                    : `${contactData?.groundStationName}, ${contactData?.groundStationProperties?.country}`
                            }
                            placement='bottom'
                        >
                            {contactData?.antennaId || contactData?.groundStationName
                                ? `${contactData.groundStationName}, ${contactData.groundStationProperties?.country}`
                                : '--'
                            }
                        </CustomToolTip>
                    </span>
                    <span className='contact__details__header__sub__title'>
                        <CustomToolTip
                            title={contactData?.providerName}
                            placement='bottom'
                        >
                            {contactData?.providerName}&nbsp;
                        </CustomToolTip>
                    </span>
                </div>

                {selected_contact && current_time && (
                    <ContactStatusBadge
                        type={selected_contact.contact_type}
                        contact={selected_contact.contact_data}
                        currentTime={current_time}
                        setIsModalOpen={setIsModalOpen}
                        dashboard={dashboard || ''}

                    />
                )}
            </div>

            {/* Map Visualization */}
            <div className='contact__visualization__miniview'>
                <Map selected_contact={selected_contact} />
                <ToggleMapButton className='small' onClick={set_show_modal}>
                    <MaximizeSvg />
                </ToggleMapButton>
            </div>

            {/* Contact Details */}
            <GroundContactDetail
                label="Contact ID"
                value={contactData?.['contactGspId'] || '--'}
            />

            {contactDetails && (
                <>
                    <GroundContactDetail
                        label="Contact Time"
                        value={contactDetails.time}
                    />
                    <GroundContactDetail
                        label="Duration"
                        value={contactDetails.duration}
                    />
                </>
            )}

            <GroundContactDetail
                label="Communication Bands"
                value={
                    <span className="band opacity-100">
                        {contactData?.['bandInfo']?.['bandName']}
                    </span>
                }
            />

            <GroundContactDetail
                label="Max. Elevation"
                value={
                    <span className="band opacity-100">
                        {contactData?.['groundStationProperties']?.['minElevation'] || "NA"}
                    </span>
                }
            />

            <div className='ground__contact__details__separator' />

            {/* Tabs Section */}
            <div className="tabs-container">
                <div className="tab-header">
                    <button
                        className={`tab-button ${activeTab === 'contacts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('contacts')}
                    >
                        CONTACTS
                    </button>
                    {deploymentType !== SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER &&
                        <button
                            className={`tab-add-button ${(!operateMode || summaryMode) ? 'invisible' : ''}`}
                            onClick={() => setIsModalOpen('create')}
                            title="Create Contact"
                        >
                            ＋
                        </button>
                    }

                </div>

                {/* Tab Content */}
                {activeTab === 'tasks' ? (
                    <div className="tab-content-contacts">
                        <div className="tasks-placeholder">
                            <p style={{ color: '#CCC', fontSize: '14px' }}>
                                Task list would go here.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="tab-content-contacts">
                        {renderContactCards()}
                    </div>
                )}
            </div>

        </div>
    );
};

export default RightSidePanel;
